import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import PatientDetailsHeader from '../../../components/Account/PatientDetailsHeader';
import MultiDatePicker from '../../../components/documented/MultiDatePicker/MultiDatePicker';
import { maxDate } from '../../../helpers/utilityFunctions/utilityFunctions';
import { useMonthlyDatePicker } from '../../../hooks/useMonthlyDatePicker';
import { setDailyMetricDate } from '../../../redux/actions/dailyMetricActions';
import {
  clearSelectedDailyParameter,
  getDailyParameterData,
  getDailyParameterDates,
} from '../../../redux/actions/dailyParameterActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { selectedMetricDate } from '../../../redux/selectors/dailyMetricsSelectors';

const useStyles = makeStyles(() => ({
  actions: {
    display: 'flex',
  },
  backButton: {
    width: '40px',
    height: '40px',
  },
  backIcon: {
    transform: 'scale(1.3)',
  },
}));

interface Props {
  patientId?: string;
}

export const ActivityTrackerDetailHeader: React.FC<Props> = ({ patientId }) => {
  const classes = useStyles();
  const { t } = useTranslation('activity');
  const navigate = useNavigate();
  const dispatch = useAppThunkDispatch();
  const selectedDailyMetricDate = useSelector(selectedMetricDate);

  // The shared "selected day" (selectedMetricDate) is deliberately persisted
  // across the daily-metrics pages, so it is honoured on first load and never
  // cleared here — only the month/day *coordination* is taken over by the hook
  // to remove the load races.
  const hadPersistedDate = !!selectedDailyMetricDate;

  const { dates, selectedDate, isLoading, setSelectedDate, setSelectedMonth } =
    useMonthlyDatePicker({
      patientId,
      getInitialDate: () =>
        selectedDailyMetricDate ? new Date(selectedDailyMetricDate) : new Date(),
      // Honour an explicit persisted date even when it has no data (existing
      // behaviour); otherwise default to the latest available day, then today.
      resolveInitialDate: (monthDates, hint) => {
        if (hadPersistedDate) return hint;
        if (monthDates.length) return maxDate(monthDates);
        return hint;
      },
      fetchMonth: (range) =>
        dispatch(
          getDailyParameterDates(patientId || '', range.startDate, range.endDate)
        ).then((action: any) => action?.payload ?? []),
      onDaySelected: ({ day, matched }) => {
        dispatch(clearSelectedDailyParameter());
        const target = matched ?? day;
        dispatch(setDailyMetricDate(target));
        dispatch(getDailyParameterData(patientId!, target));
      },
    });

  return (
    <Box>
      <PatientDetailsHeader selectedPatientId={patientId} />
      <IconButton
        sx={{ mt: 1 }}
        className={classes.backButton}
        color="primary"
        onClick={() => navigate(-1)}
      >
        <ArrowBackIcon className={classes.backIcon} />
      </IconButton>

      <Box sx={{ display: 'flex', flexDirection: 'row', mt: 1 }}>
        <Typography variant="h5" sx={{ mt: 1, pr: 2 }}>
          <strong>{t('title')}</strong>
        </Typography>

        <MultiDatePicker
          dates={dates}
          selectedDate={selectedDate}
          isLoading={isLoading}
          setSelectedDate={setSelectedDate}
          setSelectedMonth={setSelectedMonth}
        />
      </Box>
    </Box>
  );
};

export default ActivityTrackerDetailHeader;
